import { Grid, Stack } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { InfoCard, SlidingPanelContent } from 'core/components';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { IInfoCardLine } from 'core/types';
import { formatDate } from 'core/utils';
import { IExternalPricesResponse } from 'parts/types';
import { PricingHelper } from 'pricing/helpers';
import { Level } from 'theme/enums';

import { PartCostCards, VendorCostCard } from '../PartCostCards';

interface IProps {
  externalPricing?: IExternalPricesResponse | null;
  partNumber?: string;
  manufacturerId?: number;
  enabled?: boolean;
  fullWidth?: boolean;
}

interface ICostSectionData {
  cost?: number | null;
  endDate?: string | null;
  registrationNum?: string | null;
  source?: string | null;
  sourceName?: string | null;
  standardCost?: number | null;
}

const sectionGridSizing = { xs: 12, md: 6 };

const formatPricing = (amount?: number | null, uom?: string) =>
  amount != null ? PricingHelper.formatPricingPerUom(amount, uom) : DEFAULT_PLACEHOLDER;

const formatText = (value?: string | null) => value || DEFAULT_PLACEHOLDER;

const formatEndDate = (date?: string | null) => (date ? formatDate(date) : DEFAULT_PLACEHOLDER);

const buildReplacementSectionData = (
  externalPricing?: IExternalPricesResponse | null
): ICostSectionData =>
  externalPricing?.externalReplacementCost
    ? {
        cost: externalPricing.externalReplacementCost,
        endDate: externalPricing.externalRepCostEndDate,
        registrationNum: externalPricing.externalRepCostRegistrationNum,
        source: externalPricing.externalRepCostSource,
        sourceName: externalPricing.externalRepCostSourceName,
        standardCost: externalPricing.externalReplacementStandardCost,
      }
    : {
        cost: externalPricing?.purchaseCost,
        endDate: externalPricing?.externalBaseCostEndDate,
        registrationNum: externalPricing?.externalBaseCostRegistrationNum,
        source: externalPricing?.externalBaseCostSource,
        sourceName: externalPricing?.externalBaseCostSourceName,
        standardCost: externalPricing?.standardCost,
      };

const buildSpaSectionData = (
  externalPricing?: IExternalPricesResponse | null
): ICostSectionData => ({
  cost: externalPricing?.externalSpaCost,
  endDate: externalPricing?.externalSpaCostEndDate,
  registrationNum: externalPricing?.externalSpaCostRegistrationNum,
  source: externalPricing?.externalSpaCostSource,
  sourceName: externalPricing?.externalSpaCostSourceName,
  standardCost: PricingHelper.deriveSpaStandardCost(externalPricing),
});

export const AlternateCosts = ({
  externalPricing,
  partNumber,
  manufacturerId,
  enabled,
  fullWidth,
}: IProps) => {
  const { t } = useTranslation();

  const uom = externalPricing?.uom;

  const replacementData = buildReplacementSectionData(externalPricing);
  const replacementItems: IInfoCardLine[] = [
    { label: t('labelStandardCost'), value: formatPricing(replacementData.standardCost, uom) },
    { label: t('labelPurchaseCost'), value: formatPricing(replacementData.cost, uom) },
    { label: t('labelValidUntil'), value: formatEndDate(replacementData.endDate) },
    { label: t('labelReplacementCostNumber'), value: formatText(replacementData.registrationNum) },
    { label: t('labelSource'), value: formatText(replacementData.source) },
    { label: t('labelSourceName'), value: formatText(replacementData.sourceName) },
  ];

  const showSpaSection = !!externalPricing?.externalSpaCost;
  const spaData = buildSpaSectionData(externalPricing);
  const spaItems: IInfoCardLine[] = [
    { label: t('labelStandardCost'), value: formatPricing(spaData.standardCost, uom) },
    { label: t('labelExternalSpaCost'), value: formatPricing(spaData.cost, uom) },
    { label: t('labelExternalSpaCostEndDate'), value: formatEndDate(spaData.endDate) },
    { label: t('labelExternalSpaCostRegistrationNum'), value: formatText(spaData.registrationNum) },
    { label: t('labelExternalSpaCostSource'), value: formatText(spaData.source) },
    { label: t('labelExternalSpaCostSourceName'), value: formatText(spaData.sourceName) },
  ];

  const activeCostData = showSpaSection ? spaData : replacementData;
  const activeCostLabel = showSpaSection ? t('labelSpaCost') : t('labelReplacementCost');

  return (
    <SlidingPanelContent title={t('titleCosts')} fullWidth={fullWidth} scrollableContent>
      <Stack>
        <PartCostCards
          partNumber={partNumber}
          manufacturerId={manufacturerId}
          enabled={enabled}
          firstCard={
            <VendorCostCard
              groupName={activeCostLabel}
              standardCost={activeCostData.standardCost}
              purchaseCost={activeCostData.cost}
              uom={uom}
              highlighted
            />
          }
        />

        <Grid container spacing={3} sx={{ margin: '0 -10px !important' }}>
          <Grid {...sectionGridSizing}>
            <InfoCard
              title={t('labelReplacementCost')}
              titleLevel={Level.TITLE_MD}
              items={replacementItems}
            />
          </Grid>

          {showSpaSection && (
            <Grid {...sectionGridSizing}>
              <InfoCard title={t('labelSpaCost')} titleLevel={Level.TITLE_MD} items={spaItems} />
            </Grid>
          )}
        </Grid>
      </Stack>
    </SlidingPanelContent>
  );
};
